import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { useCompanyStore } from '@/stores/company'
import type {
  EmailSignature,
  EmailSignatureCreatePayload,
  EmailSignatureUpdatePayload,
} from '@/types/emailSignature'

function getCompanyId(): string {
  try {
    const companyStore = useCompanyStore()
    if (companyStore.selectedCompanyId) return companyStore.selectedCompanyId
  } catch { /* store no inicializado aún */ }
  return localStorage.getItem('selectedCompanyId') || localStorage.getItem('emailCompanyId') || ''
}

function getCurrentUserId(): string {
  try {
    const auth = FirebaseProvider.getInstance().getAuth()
    return auth.currentUser?.uid || ''
  } catch {
    return ''
  }
}

export const useEmailSignatureStore = defineStore('emailSignature', () => {
  const signatures = ref<EmailSignature[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const userSignatures = computed(() => {
    const userId = getCurrentUserId()
    return signatures.value.filter((s) => s.createdBy === userId)
  })

  const defaultSignature = computed(() => {
    return userSignatures.value.find((s) => s.isDefault) || null
  })

  function getCollectionRef() {
    const companyId = getCompanyId()
    if (!companyId) throw new Error('No se encontró companyId')
    const db = FirebaseProvider.getInstance().getFirestore()
    return collection(db, `Companies/${companyId}/emailSignatures`)
  }

  async function fetchSignatures(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const colRef = getCollectionRef()
      const q = query(colRef, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)

      signatures.value = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          name: data.name || '',
          bodyHtml: data.bodyHtml || '',
          isDefault: data.isDefault ?? false,
          createdBy: data.createdBy || '',
          createdAt: data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt || '',
          updatedBy: data.updatedBy || '',
          updatedAt: data.updatedAt instanceof Timestamp
            ? data.updatedAt.toDate().toISOString()
            : data.updatedAt || '',
        } as EmailSignature
      })
    } catch (err: any) {
      error.value = err.message || 'Error al cargar firmas'
      console.error('Error fetching signatures:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function createSignature(
    payload: EmailSignatureCreatePayload,
    userId: string
  ): Promise<string | null> {
    isLoading.value = true
    error.value = null

    try {
      if (payload.isDefault) {
        await clearDefaultForUser(userId)
      }

      const colRef = getCollectionRef()
      const now = Timestamp.now()

      const docData: Record<string, any> = {
        name: payload.name,
        bodyHtml: payload.bodyHtml,
        isDefault: payload.isDefault ?? false,
        createdBy: userId,
        createdAt: now,
        updatedBy: userId,
        updatedAt: now,
      }

      const docRef = await addDoc(colRef, docData)

      const newSignature: EmailSignature = {
        id: docRef.id,
        ...docData,
        createdAt: now.toDate().toISOString(),
        updatedAt: now.toDate().toISOString(),
      } as EmailSignature
      signatures.value.unshift(newSignature)

      return docRef.id
    } catch (err: any) {
      error.value = err.message || 'Error al crear firma'
      console.error('Error creating signature:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function updateSignature(
    id: string,
    payload: EmailSignatureUpdatePayload,
    userId: string
  ): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      if (payload.isDefault) {
        await clearDefaultForUser(userId)
      }

      const colRef = getCollectionRef()
      const docRef = doc(colRef, id)

      const updateData: Record<string, any> = {
        updatedBy: userId,
        updatedAt: Timestamp.now(),
      }

      if (payload.name !== undefined) updateData.name = payload.name
      if (payload.bodyHtml !== undefined) updateData.bodyHtml = payload.bodyHtml
      if (payload.isDefault !== undefined) updateData.isDefault = payload.isDefault

      await updateDoc(docRef, updateData)

      const idx = signatures.value.findIndex((s) => s.id === id)
      if (idx !== -1) {
        signatures.value[idx] = {
          ...signatures.value[idx],
          ...payload,
          updatedBy: userId,
          updatedAt: new Date().toISOString(),
        }
      }

      return true
    } catch (err: any) {
      error.value = err.message || 'Error al actualizar firma'
      console.error('Error updating signature:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function deleteSignature(id: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const colRef = getCollectionRef()
      const docRef = doc(colRef, id)
      await deleteDoc(docRef)

      signatures.value = signatures.value.filter((s) => s.id !== id)
      return true
    } catch (err: any) {
      error.value = err.message || 'Error al eliminar firma'
      console.error('Error deleting signature:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function setAsDefault(id: string): Promise<boolean> {
    const userId = getCurrentUserId()
    if (!userId) return false

    try {
      await clearDefaultForUser(userId)

      const colRef = getCollectionRef()
      const docRef = doc(colRef, id)
      await updateDoc(docRef, { isDefault: true, updatedBy: userId, updatedAt: Timestamp.now() })

      const idx = signatures.value.findIndex((s) => s.id === id)
      if (idx !== -1) {
        signatures.value[idx] = { ...signatures.value[idx], isDefault: true }
      }

      return true
    } catch (err: any) {
      error.value = err.message || 'Error al establecer firma predeterminada'
      console.error('Error setting default signature:', err)
      return false
    }
  }

  async function clearDefaultForUser(userId: string): Promise<void> {
    const colRef = getCollectionRef()
    const currentDefaults = signatures.value.filter(
      (s) => s.createdBy === userId && s.isDefault
    )
    for (const sig of currentDefaults) {
      const docRef = doc(colRef, sig.id)
      await updateDoc(docRef, { isDefault: false })
      const idx = signatures.value.findIndex((s) => s.id === sig.id)
      if (idx !== -1) {
        signatures.value[idx] = { ...signatures.value[idx], isDefault: false }
      }
    }
  }

  return {
    signatures,
    isLoading,
    error,
    userSignatures,
    defaultSignature,
    fetchSignatures,
    createSignature,
    updateSignature,
    deleteSignature,
    setAsDefault,
  }
})
